/**
 * ゆめさくスキルの商品カタログ (試作)
 *
 * 正本は docs/yumesaku-skills/product-catalog-v1.5.md と url-design-v1.0.md。
 * 値を変えるときは必ずドキュメント側も直す。
 *
 * 試作段階なのでここが唯一のデータ源になっている。決済と entitlement を
 * 載せる段になったら DB (products / entitlements / purchases) へ移し、
 * このファイルは型と表示ヘルパだけ残す想定。
 */

export type SupportType = "tap-30" | "tap-human-90";

export type ReleaseStage = "first" | "second" | "third" | "fourth" | "hold";

/** 単品商品 (#1〜#8)。教材の実体はここにある */
export type SkillItem = {
  /** 商品一覧の通し番号 */
  no: number;
  /** 親セットの slug */
  setSlug: string;
  /** URL 末尾。/skills/{setSlug}/{slug} */
  slug: string;
  name: string;
  /** 税込 */
  price: number;
  summary: string;
  support: SupportType;
  /** ゆめさくエンジニアの受講料への充当可否。単品は全て不可 */
  creditable: false;
  /** 元になった実践プロジェクト番号 */
  sourcePj: string;
  stage: ReleaseStage;
};

/** セット商品 (S1/S2) と一体型コース (S3〜S5) */
export type SkillSet = {
  id: string;
  slug: string;
  name: string;
  /** 税込 */
  price: number;
  /** 一体型は単品に割らない。true なら配下 item を持たない */
  monolithic: boolean;
  summary: string;
  audience: string;
  support: SupportType;
  creditable: true;
  stage: ReleaseStage;
  /**
   * 出すかどうか。false のセットは一覧に並ばず、販売LPも配下の単品LPも
   * /skills へ 302 で送る (URL設計書 §5 の S5 のガードを全商品に広げた形)。
   *
   * 試作の第一弾は WEBエンジニアコースだけを出す。決済と受講画面をこの1本で
   * 通してから、ケース4本と AI 2本を開ける
   */
  published: boolean;
};

export const SKILL_SETS: SkillSet[] = [
  {
    id: "S1",
    slug: "case-simulation",
    name: "案件シミュレーションコース",
    price: 29800,
    monolithic: false,
    summary:
      "RFP を読んで提案書を書く、移行を判断する、脆弱性報告に対応する。コードをほとんど書かずに、案件を進める側の判断を4本で通しで扱います。",
    audience: "誰でも（コードはほとんど書きません）",
    support: "tap-human-90",
    creditable: true,
    stage: "first",
    published: false,
  },
  {
    id: "S2",
    slug: "web-engineer",
    name: "WEBエンジニアコース",
    price: 98000,
    monolithic: false,
    summary:
      "EC サイト・タスク管理・AI チャット・BI ダッシュボードの4本を、設計から実装まで自分の手で作り切る教材です。Figma と動くデモ、実装 FAQ が付きます。",
    audience: "エンジニア志望の方（教材のみ・人のレビューはありません）",
    support: "tap-human-90",
    creditable: true,
    stage: "second",
    published: true,
  },
  {
    id: "S3",
    slug: "ai-sidework",
    name: "AI副業コース",
    price: 49800,
    monolithic: true,
    summary:
      "5編が続けて1本になっている一体型のカリキュラムです。AI を仕事として受ける形にするまでを扱います。",
    audience: "非エンジニア〜初級者",
    support: "tap-human-90",
    creditable: true,
    stage: "third",
    published: false,
  },
  {
    id: "S4",
    slug: "ai-agent",
    name: "AIエージェント開発コース",
    price: 49800,
    monolithic: true,
    summary:
      "AI副業コースの上位にあたる一体型コース。自社で受けた実案件を教材にして、エージェントを組み上げるところまで進みます。",
    audience: "AI副業コース修了者・API を触ったことがある方",
    support: "tap-human-90",
    creditable: true,
    stage: "fourth",
    published: false,
  },
  {
    id: "S5",
    slug: "ai-design",
    name: "AI×デザインコース",
    price: 49800,
    monolithic: true,
    summary: "一体型コース。採用が決まるまで公開しません。",
    audience: "非エンジニア・デザイン志向の方",
    support: "tap-human-90",
    creditable: true,
    stage: "hold",
    published: false,
  },
];

export const SKILL_ITEMS: SkillItem[] = [
  {
    no: 1,
    setSlug: "case-simulation",
    slug: "dx-cafe",
    name: "ケース① DX提案（カフェ企業編）",
    price: 9800,
    summary: "RFP を読み解いて、DX 提案書を1本書き上げます。",
    support: "tap-30",
    creditable: false,
    sourcePj: "PJ⑤",
    stage: "first",
  },
  {
    no: 2,
    setSlug: "case-simulation",
    slug: "dx-school",
    name: "ケース② DX提案（スクール企業編）",
    price: 9800,
    summary: "業種の違う RFP をもう1本読み解きます。",
    support: "tap-30",
    creditable: false,
    sourcePj: "PJ⑥",
    stage: "first",
  },
  {
    no: 3,
    setSlug: "case-simulation",
    slug: "infra-migration",
    name: "ケース③ インフラ移行",
    price: 14800,
    summary: "VPS からクラウドへ移すかどうかを判断し、移行計画書にまとめます。",
    support: "tap-30",
    creditable: false,
    sourcePj: "PJ⑦",
    stage: "first",
  },
  {
    no: 4,
    setSlug: "case-simulation",
    slug: "security-incident",
    name: "ケース④ セキュリティ対応",
    price: 14800,
    summary: "届いた脆弱性報告にどう対応するかを決め、報告書を書きます。",
    support: "tap-30",
    creditable: false,
    sourcePj: "PJ⑧",
    stage: "first",
  },
  {
    no: 5,
    setSlug: "web-engineer",
    slug: "ec-site",
    name: "実践① ECサイト開発",
    price: 49800,
    summary:
      "Java / JSP / PostgreSQL で、カートから決済までをフルスタックで実装します。",
    support: "tap-30",
    creditable: false,
    sourcePj: "PJ①",
    stage: "second",
  },
  {
    no: 6,
    setSlug: "web-engineer",
    slug: "task-app",
    name: "実践② タスク管理アプリ",
    price: 49800,
    summary:
      "Node.js / Prisma / React で、JWT 認証つきの業務 SaaS を組み上げます。",
    support: "tap-30",
    creditable: false,
    sourcePj: "PJ②",
    stage: "second",
  },
  {
    no: 7,
    setSlug: "web-engineer",
    slug: "ai-chat",
    name: "実践③ AIチャットアプリ",
    price: 49800,
    summary:
      "TypeScript / React / OpenAI API で、LLM 統合をフルスタックで構築します。",
    support: "tap-30",
    creditable: false,
    sourcePj: "PJ③",
    stage: "second",
  },
  {
    no: 8,
    setSlug: "web-engineer",
    slug: "bi-dashboard",
    name: "実践④ BIダッシュボード",
    price: 49800,
    summary: "SQL の集計と Recharts で、社内 BI を1枚仕上げます。",
    support: "tap-30",
    creditable: false,
    sourcePj: "PJ④",
    stage: "second",
  },
];

export const SUPPORT_LABEL: Record<SupportType, string> = {
  "tap-30": "Tapのみ・30日",
  "tap-human-90": "Tap＋人・90日",
};

export const STAGE_LABEL: Record<ReleaseStage, string> = {
  first: "初荷",
  second: "2番目",
  third: "3番目",
  fourth: "4番目",
  hold: "保留",
};

export function findSet(slug: string): SkillSet | undefined {
  return SKILL_SETS.find((s) => s.slug === slug);
}

export function findItem(
  setSlug: string,
  itemSlug: string
): SkillItem | undefined {
  return SKILL_ITEMS.find((i) => i.setSlug === setSlug && i.slug === itemSlug);
}

export function itemsOfSet(setSlug: string): SkillItem[] {
  return SKILL_ITEMS.filter((i) => i.setSlug === setSlug);
}

/** 一覧に並べるセット */
export function publishedSets(): SkillSet[] {
  return SKILL_SETS.filter((s) => s.published);
}

/** 一覧に並べる単品。親セットが出ていないものは出さない */
export function publishedItems(): SkillItem[] {
  const open = new Set(publishedSets().map((s) => s.slug));
  return SKILL_ITEMS.filter((i) => open.has(i.setSlug));
}

/** 配下の単品を全部買った場合の合計。一体型は 0 を返す */
export function itemsTotal(setSlug: string): number {
  return itemsOfSet(setSlug).reduce((sum, i) => sum + i.price, 0);
}

/** セットにした場合の割引率（%・切り捨て）。比較対象が無ければ null */
export function setDiscountRate(set: SkillSet): number | null {
  const total = itemsTotal(set.slug);
  if (total <= set.price) return null;
  return Math.floor(((total - set.price) / total) * 100);
}

export function formatPrice(yen: number): string {
  return `${yen.toLocaleString("ja-JP")}円`;
}
